/** Story crop — OnlyFans-style: fixed aspect frame + blurred letterbox fill + rotate. */

export type AspectId = '9:16' | '2:3' | '3:4' | '1:1' | '16:9' | '3:2' | '4:3'

export const PORTRAIT_ASPECTS: AspectId[] = ['9:16', '2:3', '3:4', '1:1']
export const LANDSCAPE_ASPECTS: AspectId[] = ['16:9', '3:2', '4:3', '1:1']

const ASPECT_VALUE: Record<AspectId, number> = {
  '9:16': 9 / 16,
  '2:3': 2 / 3,
  '3:4': 3 / 4,
  '1:1': 1,
  '16:9': 16 / 9,
  '3:2': 3 / 2,
  '4:3': 4 / 3,
}

const FLIP: Record<AspectId, AspectId> = {
  '9:16': '16:9',
  '2:3': '3:2',
  '3:4': '4:3',
  '1:1': '1:1',
  '16:9': '9:16',
  '3:2': '2:3',
  '4:3': '3:4',
}

export function aspectValue(id: AspectId): number {
  return ASPECT_VALUE[id]
}

export function flipAspect(id: AspectId): AspectId {
  return FLIP[id]
}

export function aspectsForRotation(deg: number): AspectId[] {
  const q = ((deg % 360) + 360) % 360
  return q === 90 || q === 270 ? LANDSCAPE_ASPECTS : PORTRAIT_ASPECTS
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to read image'))
    img.src = src
  })
}

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360
}

/** Rasterize `img` rotated by 0/90/180/270 into a new canvas. */
export function rotateSource(img: HTMLImageElement, deg: number): HTMLCanvasElement {
  const q = normalizeDeg(deg)
  const sw = img.naturalWidth
  const sh = img.naturalHeight
  const canvas = document.createElement('canvas')
  if (q === 90 || q === 270) {
    canvas.width = sh
    canvas.height = sw
  } else {
    canvas.width = sw
    canvas.height = sh
  }
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((q * Math.PI) / 180)
  ctx.drawImage(img, -sw / 2, -sh / 2)
  return canvas
}

/**
 * Bake OF story crop: canvas at `aspect`, blurred cover fill, sharp image contain-centered, optional rotate.
 * Output max edge 1080px.
 */
export async function bakeStoryCrop(file: File, aspect: AspectId, rotation = 0): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const src = rotateSource(img, rotation)
    const srcW = src.width
    const srcH = src.height
    const ratio = aspectValue(aspect)
    const maxEdge = 1080
    const outW = ratio >= 1 ? maxEdge : Math.max(1, Math.round(maxEdge * ratio))
    const outH = ratio >= 1 ? Math.max(1, Math.round(maxEdge / ratio)) : maxEdge

    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')
    if (!ctx) return file

    const cover = Math.max(outW / srcW, outH / srcH)
    const cw = srcW * cover
    const ch = srcH * cover
    ctx.save()
    ctx.filter = 'blur(28px)'
    ctx.drawImage(src, (outW - cw) / 2, (outH - ch) / 2, cw, ch)
    ctx.restore()
    ctx.fillStyle = 'rgba(0,0,0,0.18)'
    ctx.fillRect(0, 0, outW, outH)

    const fit = Math.min(outW / srcW, outH / srcH)
    const fw = srcW * fit
    const fh = srcH * fit
    ctx.drawImage(src, (outW - fw) / 2, (outH - fh) / 2, fw, fh)

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (!blob) return file
    return new File([blob], file.name.replace(/\.\w+$/, '') + '-crop.jpg', { type: 'image/jpeg' })
  } finally {
    URL.revokeObjectURL(url)
  }
}
